const MAX_ITEMS = 5;

class CartUpsellComponent extends HTMLElement {
  #cachedItems = null;
  #icons = { bag: '', prev: '', next: '' };
  #resizeObserver = null;

  #mutationObserver = new MutationObserver((mutations) => {
    // The cart re-render can change several attributes at once. A single data change invalidates
    // the cache for the whole batch, so the `hidden` toggle never replays stale recommendations.
    const dataChanged = mutations.some((mutation) => mutation.attributeName !== 'hidden');

    if (dataChanged) {
      this.#cachedItems = null;
      CartUpsellComponent.#clearSessionCache(this.dataset.productId);
      this.#loadRecommendation();
      return;
    }

    if (this.hasAttribute('hidden') && this.#cachedItems?.length) {
      this.#renderFromCache();
    }
  });

  connectedCallback() {
    this.#cacheIcons();
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: [
        'data-product-id',
        'data-cart-variant-ids',
        'data-frequency-option',
        'data-cart-frequencies',
        'hidden',
      ],
    });
    this.#loadRecommendation();
  }

  disconnectedCallback() {
    this.#mutationObserver.disconnect();
    this.#resizeObserver?.disconnect();
  }

  #cacheIcons() {
    const read = (name) =>
      this.querySelector(`template[data-icon="${name}"]`)?.innerHTML ?? '';
    this.#icons = { bag: read('bag'), prev: read('prev'), next: read('next') };
  }

  #renderFromCache() {
    this.removeAttribute('hidden');
    this.#render(this.#cachedItems);
  }

  async #loadRecommendation() {
    const { productId, cartVariantIds } = this.dataset;

    if (!productId) return;

    const cartVariants = new Set((cartVariantIds || '').split(',').filter(Boolean));

    try {
      let products = await this.#fetchProducts(productId, cartVariants, 'complementary');
      if (products.length === 0) {
        products = await this.#fetchProducts(productId, cartVariants, 'related');
      }

      // With a single frequency in the cart every card uses it. With both, the cards alternate so
      // each frequency in the cart gets represented instead of every card falling on the first one.
      const frequencies = [...this.#cartFrequencies()];

      const items = [];
      for (const product of products) {
        const preferred = frequencies.length
          ? frequencies[items.length % frequencies.length]
          : null;
        const variant = this.#pickVariant(product, cartVariants, preferred);
        if (variant) items.push({ product, variant });
        if (items.length >= MAX_ITEMS) break;
      }

      if (items.length === 0) {
        this.setAttribute('hidden', '');
        return;
      }

      this.#cachedItems = items;
      this.removeAttribute('hidden');
      this.#render(items);
    } catch {
      this.setAttribute('hidden', '');
    }
  }

  async #fetchProducts(productId, cartVariants, intent) {
    const cacheKey = `cart-upsell:${productId}:${intent}`;

    let all = null;
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) all = JSON.parse(raw);
    } catch {}

    if (!all) {
      const response = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=${MAX_ITEMS}&intent=${intent}`
      );
      if (!response.ok) return [];
      const data = await response.json();
      all = data.products;
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(all));
      } catch {}
    }

    return all.filter((p) => this.#pickVariant(p, cartVariants) != null);
  }

  /**
   * Picks the variant to offer for a recommended product.
   *
   * Only a variant whose purchase frequency (e.g. "Compra única" or "Mensal") is already present
   * in the cart is eligible, so the card adds the item on a plan the customer is already using. A
   * cart with a single frequency restricts the offer to it; a cart holding both accepts both.
   * Products that offer a frequency but none of the cart's is skipped entirely, and products
   * without the frequency option (accessories, one-off items) stay eligible.
   *
   * `preferred` (already normalized) breaks the tie when more than one variant is eligible.
   */
  #pickVariant(product, cartVariants, preferred = null) {
    const optionIndex = this.#frequencyOptionIndex(product);
    const wanted = this.#cartFrequencies();

    const eligible = product.variants.filter((variant) => {
      if (!variant.available || cartVariants.has(String(variant.id))) return false;
      if (optionIndex === -1 || wanted.size === 0) return true;

      return wanted.has(this.#variantFrequency(variant, optionIndex));
    });

    if (eligible.length === 0) return undefined;
    if (!preferred || optionIndex === -1) return eligible[0];

    return (
      eligible.find((variant) => this.#variantFrequency(variant, optionIndex) === preferred) ??
      eligible[0]
    );
  }

  /** Frequency value of a variant, in comparison form. */
  #variantFrequency(variant, optionIndex) {
    const value = variant.options?.[optionIndex] ?? variant[`option${optionIndex + 1}`] ?? '';
    return CartUpsellComponent.#normalize(value);
  }

  /** Distinct purchase frequencies present in the cart, in comparison form. */
  #cartFrequencies() {
    const values = (this.dataset.cartFrequencies || '')
      .split('|')
      .map((value) => CartUpsellComponent.#normalize(value))
      .filter(Boolean);

    return new Set(values);
  }

  /** Position of the frequency option within the product's options, or -1 when absent. */
  #frequencyOptionIndex(product) {
    const name = CartUpsellComponent.#normalize(this.dataset.frequencyOption || '');
    if (!name) return -1;

    return (product.options || []).findIndex((option) => {
      const optionName = typeof option === 'string' ? option : (option?.name ?? '');
      return CartUpsellComponent.#normalize(optionName) === name;
    });
  }

  /**
   * Comparison form for option names and values: the catalog mixes casing and accent encodings
   * ("Compra Única" and "Compra única" both exist), so compare without case, accents or extra
   * whitespace.
   */
  static #normalize(value) {
    return String(value)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  #render(items) {
    const multi = items.length > 1;
    this.classList.toggle('cart-upsell--multi', multi);

    const cards = items.map((item) => this.#cardHtml(item)).join('');

    const label = (this.dataset.label || '').trim();
    const labelHtml = label
      ? `<p class="cart-upsell__label">${this.#escapeHtml(label)}</p>`
      : '';

    this.innerHTML = `
      ${labelHtml}
      <div class="cart-upsell__carousel">
        ${
          multi
            ? `<button type="button" class="cart-upsell__arrow cart-upsell__arrow--prev" aria-label="Anterior">${this.#icons.prev}</button>`
            : ''
        }
        <div class="cart-upsell__track">${cards}</div>
        ${
          multi
            ? `<button type="button" class="cart-upsell__arrow cart-upsell__arrow--next" aria-label="Próximo">${this.#icons.next}</button>`
            : ''
        }
      </div>
    `;

    this.#wire();
  }

  #cardHtml({ product, variant }) {
    const title = product.title || '';
    const image = product.featured_image || '';
    const price = this.#formatMoney(variant.price);
    const onSale = variant.compare_at_price > variant.price;
    const comparePrice = onSale ? this.#formatMoney(variant.compare_at_price) : null;
    const percent = onSale
      ? Math.round((1 - variant.price / variant.compare_at_price) * 100)
      : null;

    return `
      <article class="cart-upsell__card" data-variant-id="${variant.id}">
        <div class="cart-upsell__image-wrapper">
          ${
            image
              ? `<img
                  class="cart-upsell__image"
                  src="${image}&width=120"
                  alt="${this.#escapeHtml(title)}"
                  width="56"
                  height="56"
                  loading="lazy"
                >`
              : ''
          }
        </div>
        <div class="cart-upsell__info">
          <p class="cart-upsell__title">${this.#escapeHtml(title)}</p>
          <div class="cart-upsell__price">
            ${comparePrice ? `<span class="cart-upsell__compare-price">${comparePrice}</span>` : ''}
            <span class="cart-upsell__current-price">${price}</span>
            ${percent != null ? `<span class="cart-upsell__badge">${percent}%OFF</span>` : ''}
          </div>
        </div>
        <button
          type="button"
          class="cart-upsell__button"
          data-variant-id="${variant.id}"
          aria-label="Adicionar"
        >
          ${this.#icons.bag}
        </button>
      </article>
    `;
  }

  #wire() {
    this.querySelectorAll('.cart-upsell__button').forEach((button) => {
      button.addEventListener('click', () => {
        this.#addToCart(button.dataset.variantId, button.closest('.cart-upsell__card'));
      });
    });

    this.#resizeObserver?.disconnect();

    const track = this.querySelector('.cart-upsell__track');
    const prev = this.querySelector('.cart-upsell__arrow--prev');
    const next = this.querySelector('.cart-upsell__arrow--next');

    if (!track || !prev || !next) return;

    const step = () => {
      const card = track.querySelector('.cart-upsell__card');
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return card ? card.getBoundingClientRect().width + gap : track.clientWidth;
    };

    const updateArrows = () => {
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    };

    prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', updateArrows, { passive: true });

    this.#resizeObserver = new ResizeObserver(() => updateArrows());
    this.#resizeObserver.observe(track);

    updateArrows();
  }

  async #addToCart(variantId, card) {
    const button = card?.querySelector('.cart-upsell__button');
    if (!button || button.disabled) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (!response.ok) {
        this.#resetButton(button);
        return;
      }

      const cart = await response.json();

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, sourceId: this.id, data: {} },
        })
      );

      card.classList.add('cart-upsell__card--added');
      card.addEventListener(
        'transitionend',
        () => {
          card.remove();
          if (!this.querySelector('.cart-upsell__card')) {
            this.setAttribute('hidden', '');
          }
        },
        { once: true }
      );
    } catch {
      this.#resetButton(button);
    }
  }

  #resetButton(button) {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }

  #formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    return new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'BRL',
      minimumFractionDigits: 2,
    }).format(cents / 100);
  }

  #escapeHtml(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  static #clearSessionCache(productId) {
    if (!productId) return;
    try {
      sessionStorage.removeItem(`cart-upsell:${productId}:complementary`);
      sessionStorage.removeItem(`cart-upsell:${productId}:related`);
    } catch {}
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
